import { useMemo } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import type { Database } from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import type { ZeroMutateClient } from "./useZeroMutationUtils";

export type PackingState = Database["public"]["Enums"]["packing_state"];
export type RiggingState = Database["public"]["Enums"]["rigging_state"];

export type ProjectEquipmentGroup = {
  id: string;
  project_id: string;
  name: string;
  packing_state: PackingState;
  rigging_state: RiggingState;
  display_order: number | null;
  is_collapsed: boolean | null;
  created_at: string;
  updated_at: string;
};

export type TemporaryEquipment = {
  id: string;
  project_id: string;
  name: string;
  country_of_origin: string | null;
  carnet_ata_description: string | null;
  carnet_ata_value: number | null;
  weight: number | null;
  carnet_ata_weight_unit: string | null;
  created_at: string;
  updated_at: string;
};

export type ProjectEquipmentAssignment = {
  id: string;
  group_id: string;
  equipment_id: string | null;
  temporary_equipment_id: string | null;
  amount: number;
  notes: string | null;
  camera_plan_column_key: string | null;
  created_at: string;
  updated_at: string;
  equipment?: {
    id: string;
    name: string;
    short_name: string | null;
    number: string | null;
    manufacturer: string | null;
    is_serialized: boolean;
    weight: number | null;
    new_price: number | null;
    country_of_origin: string | null;
    customs_tariff_number: string | null;
    carnet_ata_description: string | null;
    carnet_ata_value: number | null;
    carnet_ata_weight_unit: string | null;
    equipment_tag_assignments?: { tag_id: string }[];
  } | null;
  temporary_equipment?: TemporaryEquipment | null;
  instance_assignments?: {
    id: string;
    instance_id: string;
    instance?: {
      id: string;
      identifier: string;
      serial_number: string | null;
      status: string;
    };
  }[];
};

export type ProjectEquipmentGroupWithAssignments = ProjectEquipmentGroup & {
  assignments: ProjectEquipmentAssignment[];
};

type GroupRow = ReturnType<
  typeof appQueries.equipmentPlan.byProject
>["~"]["$return"];
type MutationOptions<T> = { onSuccess?: (data: T) => void };
type EquipmentAssignmentRow = {
  id: string;
  groupID: string;
  equipmentID?: string | null;
  temporaryEquipmentID?: string | null;
  amount: number;
  notes?: string | null;
  cameraPlanColumnKey?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  equipment?: {
    id: string;
    name: string;
    shortName?: string | null;
    number?: string | null;
    manufacturer?: string | null;
    isSerialized: boolean;
    weight?: number | null;
    newPrice?: number | null;
    countryOfOrigin?: string | null;
    customsTariffNumber?: string | null;
    carnetAtaDescription?: string | null;
    carnetAtaValue?: number | null;
    carnetAtaWeightUnit?: string | null;
    tagAssignments?: { tagID: string }[];
  } | null;
  temporaryEquipment?: {
    id: string;
    projectID: string;
    name: string;
    countryOfOrigin?: string | null;
    carnetAtaDescription?: string | null;
    carnetAtaValue?: number | null;
    weight?: number | null;
    carnetAtaWeightUnit?: string | null;
    createdAt?: number | string | null;
    updatedAt?: number | string | null;
  } | null;
  instanceAssignments?: {
    id: string;
    instanceID: string;
    instance?: {
      id: string;
      identifier: string;
      serialNumber?: string | null;
      status: string;
    } | null;
  }[];
};
type EquipmentGroupRow = {
  id: string;
  projectID: string;
  name: string;
  packingState: PackingState;
  riggingState: RiggingState;
  displayOrder?: number | null;
  isCollapsed?: boolean | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  assignments?: EquipmentAssignmentRow[];
};

function toLegacyTimestamp(value: number | string | null | undefined): string {
  if (typeof value === "number") {
    return new Date(value).toISOString();
  }
  return value ?? "";
}

function toLegacyAssignment(
  row: EquipmentAssignmentRow,
): ProjectEquipmentAssignment {
  return {
    id: row.id,
    group_id: row.groupID,
    equipment_id: row.equipmentID ?? null,
    temporary_equipment_id: row.temporaryEquipmentID ?? null,
    amount: row.amount,
    notes: row.notes ?? null,
    camera_plan_column_key: row.cameraPlanColumnKey ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    equipment: row.equipment
      ? {
          id: row.equipment.id,
          name: row.equipment.name,
          short_name: row.equipment.shortName ?? null,
          number: row.equipment.number ?? null,
          manufacturer: row.equipment.manufacturer ?? null,
          is_serialized: row.equipment.isSerialized,
          weight: row.equipment.weight ?? null,
          new_price: row.equipment.newPrice ?? null,
          country_of_origin: row.equipment.countryOfOrigin ?? null,
          customs_tariff_number: row.equipment.customsTariffNumber ?? null,
          carnet_ata_description: row.equipment.carnetAtaDescription ?? null,
          carnet_ata_value: row.equipment.carnetAtaValue ?? null,
          carnet_ata_weight_unit: row.equipment.carnetAtaWeightUnit ?? null,
          equipment_tag_assignments:
            row.equipment.tagAssignments?.map((tag) => ({
              tag_id: tag.tagID,
            })) ?? [],
        }
      : null,
    temporary_equipment: row.temporaryEquipment
      ? {
          id: row.temporaryEquipment.id,
          project_id: row.temporaryEquipment.projectID,
          name: row.temporaryEquipment.name,
          country_of_origin: row.temporaryEquipment.countryOfOrigin ?? null,
          carnet_ata_description:
            row.temporaryEquipment.carnetAtaDescription ?? null,
          carnet_ata_value: row.temporaryEquipment.carnetAtaValue ?? null,
          weight: row.temporaryEquipment.weight ?? null,
          carnet_ata_weight_unit:
            row.temporaryEquipment.carnetAtaWeightUnit ?? null,
          created_at: toLegacyTimestamp(row.temporaryEquipment.createdAt),
          updated_at: toLegacyTimestamp(row.temporaryEquipment.updatedAt),
        }
      : null,
    instance_assignments:
      row.instanceAssignments?.map((assignment) => ({
        id: assignment.id,
        instance_id: assignment.instanceID,
        instance: assignment.instance
          ? {
              id: assignment.instance.id,
              identifier: assignment.instance.identifier,
              serial_number: assignment.instance.serialNumber ?? null,
              status: assignment.instance.status,
            }
          : undefined,
      })) ?? [],
  };
}

function toLegacyGroup(
  row: EquipmentGroupRow,
): ProjectEquipmentGroupWithAssignments {
  return {
    id: row.id,
    project_id: row.projectID,
    name: row.name,
    packing_state: row.packingState,
    rigging_state: row.riggingState,
    display_order: row.displayOrder ?? null,
    is_collapsed: row.isCollapsed ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    assignments: [...(row.assignments ?? [])].map(toLegacyAssignment),
  };
}

export function useProjectEquipmentGroups(projectId: string | undefined) {
  const query = projectId
    ? appQueries.equipmentPlan.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });

  const groups = useMemo(
    () => ((data ?? []) as GroupRow[]).map(toLegacyGroup),
    [data],
  );

  return {
    data: groups,
    isLoading: details.type !== "complete" && groups.length === 0,
  };
}

export function useCreateEquipmentGroup() {
  const zero = useZero() as ZeroMutateClient;
  const run = async (v: {
    project_id: string;
    name: string;
    display_order?: number;
    optimisticId?: string;
  }) => {
    const group: ProjectEquipmentGroupWithAssignments = {
      id: v.optimisticId ?? generateUUID(),
      project_id: v.project_id,
      name: v.name,
      packing_state: "open",
      rigging_state: "open",
      display_order: v.display_order ?? 0,
      is_collapsed: false,
      created_at: "",
      updated_at: "",
      assignments: [],
    };
    await zero.mutate(
      appMutators.equipmentPlan.createGroup({
        id: group.id,
        projectID: v.project_id,
        name: v.name,
        displayOrder: v.display_order ?? null,
      }),
    ).client;
    return group;
  };
  return {
    mutate: (
      v: Parameters<typeof run>[0],
      options?: MutationOptions<ProjectEquipmentGroupWithAssignments>,
    ) => {
      void run(v).then((result) => options?.onSuccess?.(result));
    },
    mutateAsync: run,
  };
}

export function useCopyEquipmentPlanning() {
  const zero = useZero() as ZeroMutateClient;
  const run = async (v: {
    targetProjectId: string;
    sourceProjectId: string;
    mapCameraAssignments?: boolean;
  }) => {
    const mutation = zero.mutate(
      appMutators.copyActions.copyEquipmentPlanning({
        targetProjectID: v.targetProjectId,
        sourceProjectID: v.sourceProjectId,
        mapCameraAssignments: v.mapCameraAssignments ?? true,
      }),
    );
    await mutation.client;
    const result = await mutation.server;
    if (result?.type === "error") {
      throw new Error(
        result.error?.message || "Equipment planning copy rejected",
      );
    }
  };
  return {
    mutateAsync: run,
    isPending: false,
  };
}

export function useUpdateEquipmentGroup() {
  const zero = useZero() as ZeroMutateClient;
  return {
    mutate: (v: {
      id: string;
      projectId: string;
      updates: Partial<ProjectEquipmentGroup>;
    }) => {
      void zero.mutate(
        appMutators.equipmentPlan.updateGroup({
          id: v.id,
          name: v.updates.name,
          packingState: v.updates.packing_state,
          riggingState: v.updates.rigging_state,
          displayOrder: v.updates.display_order,
          isCollapsed: v.updates.is_collapsed,
        }),
      ).client;
    },
  };
}

export function useDeleteEquipmentGroup() {
  const zero = useZero() as ZeroMutateClient;
  const run = async (v: { id: string; projectId: string }) => {
    await zero.mutate(appMutators.equipmentPlan.deleteGroup({ id: v.id }))
      .client;
  };
  return {
    mutate: (v: { id: string; projectId: string }) => void run(v),
    mutateAsync: run,
  };
}

export function useCreateTemporaryEquipment() {
  const zero = useZero() as ZeroMutateClient;
  return {
    mutateAsync: async (v: {
      project_id: string;
      name: string;
      country_of_origin?: string | null;
      carnet_ata_description?: string | null;
      carnet_ata_value?: number | null;
      weight?: number | null;
      carnet_ata_weight_unit?: string | null;
    }) => {
      const id = generateUUID();
      await zero.mutate(
        appMutators.equipmentPlan.createTemporaryEquipment({
          id,
          projectID: v.project_id,
          name: v.name,
          countryOfOrigin: v.country_of_origin ?? null,
          carnetAtaDescription: v.carnet_ata_description ?? null,
          carnetAtaValue: v.carnet_ata_value ?? null,
          weight: v.weight ?? null,
          carnetAtaWeightUnit: v.carnet_ata_weight_unit ?? null,
        }),
      ).client;
      return {
        id,
        project_id: v.project_id,
        name: v.name,
        country_of_origin: v.country_of_origin ?? null,
        carnet_ata_description: v.carnet_ata_description ?? null,
        carnet_ata_value: v.carnet_ata_value ?? null,
        weight: v.weight ?? null,
        carnet_ata_weight_unit: v.carnet_ata_weight_unit ?? null,
        created_at: "",
        updated_at: "",
      } satisfies TemporaryEquipment;
    },
  };
}

export function useUpdateTemporaryEquipment() {
  const zero = useZero() as {
    mutate: (mutation: unknown) => { client: Promise<unknown> };
  };
  return {
    mutate: (v: {
      id: string;
      name?: string;
      country_of_origin?: string | null;
      carnet_ata_description?: string | null;
      carnet_ata_value?: number | null;
      weight?: number | null;
      carnet_ata_weight_unit?: string | null;
    }) => {
      void zero.mutate(
        appMutators.equipmentPlan.updateTemporaryEquipment({
          id: v.id,
          name: v.name,
          countryOfOrigin: v.country_of_origin,
          carnetAtaDescription: v.carnet_ata_description,
          carnetAtaValue: v.carnet_ata_value,
          weight: v.weight,
          carnetAtaWeightUnit: v.carnet_ata_weight_unit,
        }),
      ).client;
    },
  };
}

export function useCreateEquipmentAssignment() {
  const zero = useZero() as ZeroMutateClient;
  const run = async (v: {
    group_id: string;
    equipment_id?: string;
    temporary_equipment_id?: string;
    amount?: number;
    notes?: string;
    camera_plan_column_key?: string;
    projectId: string;
    optimisticId?: string;
  }) => {
    const id = v.optimisticId ?? generateUUID();
    await zero.mutate(
      appMutators.equipmentPlan.createAssignment({
        id,
        groupID: v.group_id,
        equipmentID: v.equipment_id ?? null,
        temporaryEquipmentID: v.temporary_equipment_id ?? null,
        amount: v.amount ?? 1,
        notes: v.notes ?? null,
        cameraPlanColumnKey: v.camera_plan_column_key ?? null,
      }),
    ).client;
    return { id } as ProjectEquipmentAssignment;
  };
  return {
    mutate: (
      v: Parameters<typeof run>[0],
      options?: MutationOptions<ProjectEquipmentAssignment>,
    ) => {
      void run(v).then((result) => options?.onSuccess?.(result));
    },
    mutateAsync: run,
  };
}

export function useUpdateEquipmentAssignment() {
  const zero = useZero() as ZeroMutateClient;
  return {
    mutate: (v: {
      id: string;
      projectId: string;
      updates: {
        amount?: number;
        notes?: string;
        equipment_id?: string | null;
        temporary_equipment_id?: string | null;
      };
    }) => {
      void zero.mutate(
        appMutators.equipmentPlan.updateAssignment({
          id: v.id,
          amount: v.updates.amount,
          notes: v.updates.notes,
          equipmentID: v.updates.equipment_id,
          temporaryEquipmentID: v.updates.temporary_equipment_id,
        }),
      ).client;
    },
  };
}

export function useDeleteEquipmentAssignment() {
  const zero = useZero() as ZeroMutateClient;
  const run = async (v: { id: string; projectId: string }) => {
    await zero.mutate(appMutators.equipmentPlan.deleteAssignment({ id: v.id }))
      .client;
  };
  return {
    mutate: (v: { id: string; projectId: string }) => void run(v),
    mutateAsync: run,
  };
}

export function useAssignInstance() {
  const zero = useZero() as ZeroMutateClient;
  return {
    mutate: (v: {
      assignmentId: string;
      instanceId: string;
      projectId: string;
      instancePreview?: { id: string; identifier: string; status: string };
    }) => {
      void zero.mutate(
        appMutators.equipmentPlan.assignInstance({
          id: generateUUID(),
          assignmentID: v.assignmentId,
          instanceID: v.instanceId,
        }),
      ).client;
    },
  };
}

export function useUnassignInstance() {
  const zero = useZero() as ZeroMutateClient;
  const run = async (v: { id: string; projectId: string }) => {
    await zero.mutate(appMutators.equipmentPlan.unassignInstance({ id: v.id }))
      .client;
  };
  return {
    mutate: (v: { id: string; projectId: string }) => void run(v),
    mutateAsync: run,
  };
}
